import { Inject, Injectable } from "@angular/core";
import { Link } from "@common/ADAPT.Common.Model/organisation/link";
import { Meeting } from "@common/ADAPT.Common.Model/organisation/meeting";
import { MeetingAgendaItem } from "@common/ADAPT.Common.Model/organisation/meeting-agenda-item";
import { MeetingAgendaTemplate } from "@common/ADAPT.Common.Model/organisation/meeting-agenda-template";
import { MeetingNoteType, MeetingNoteTypeMetadata } from "@common/ADAPT.Common.Model/organisation/meeting-note";
import { AdaptClientConfiguration, AdaptProject } from "@common/configuration/adapt-client-configuration";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { AdaptError } from "@common/lib/error-handler/adapt-error";
import { FunctionUtilities } from "@common/lib/utilities/function-utilities";
import { TEAM_DASHBOARD_PAGE } from "@common/page-route-providers";
import { NavigationHierarchyService } from "@common/route/navigation-hierarchy.service";
import { IAdaptRoute } from "@common/route/page-route-builder";
import { RouteService } from "@common/route/route.service";
import { ShellUiService } from "@common/shell/shell-ui.service";
import { AdaptCommonDialogService } from "@common/ux/adapt-common-dialog/adapt-common-dialog.service";
import { IConfirmationDialogData } from "@common/ux/adapt-common-dialog/confirmation-dialog.component/confirmation-dialog.component";
import { EditSimpleValueBreezeEntityDialogComponent, IEditSimpleValueBreezeEntityDialogData, SimpleValueType } from "@common/ux/edit-simple-value-breeze-entity-dialog/edit-simple-value-breeze-entity-dialog.component";
import { CalendarIntegrationUtilities } from "@org-common/lib/calendar/calendar-integration-utilities";
import { MeetingIntegrationImportDialogComponent } from "@org-common/lib/meetings/meeting-integration-import-dialog/meeting-integration-import-dialog.component";
import { NewMeetingTemplateDialogComponent } from "@org-common/lib/meetings/new-meeting-template-dialog/new-meeting-template-dialog.component";
import { OrganisationService } from "@org-common/lib/organisation/organisation.service";
import { BehaviorSubject, EMPTY, forkJoin, of, throwError } from "rxjs";
import { catchError, filter, map, switchMap, take, tap } from "rxjs/operators";
import { ICalendarProvider } from "../calendar/calendar.interface";
import { SelectItemDialogComponent } from "../kanban/items/select-item-dialog/select-item-dialog.component";
import { KanbanAuthService } from "../kanban/kanban-auth.service";
import { teamKanbanPageRoute } from "../kanban/kanban-page/kanban-page.route";
import { LinkService } from "../link/link.service";
import { AddObjectiveLinkDialogComponent, IAddObjectiveLinkDialogData } from "../objectives/add-objective-link-dialog.component/add-objective-link-dialog.component";
import { MeetingTabId } from "../shell/meeting-tab-content/meeting-tab-id";
import { MenuTabId } from "../shell/menu-tab-content/menu-tab-id";
import { TeamTabId } from "../shell/team-tab-content/team-tab-id";
import { WorkflowService } from "../workflow/workflow.service";
import { WorkflowMeetingService } from "../workflow/workflow-meeting.service";
import { WorkflowRunDialogComponent } from "../workflow/workflow-run-dialog/workflow-run-dialog.component";
import { EditAgendaItemDialogComponent } from "./edit-agenda-item-dialog/edit-agenda-item-dialog.component";
import { EditAgendaTemplateDialogComponent } from "./edit-agenda-template-dialog/edit-agenda-template-dialog.component";
import { EditMeetingAgendaDialogComponent } from "./edit-meeting-agenda-dialog/edit-meeting-agenda-dialog.component";
import { IImportMeetingAgendaData, ImportAgendaDialogComponent } from "./import-agenda-dialog/import-agenda-dialog.component";
import { MeetingPreWorkDialogComponent } from "./meeting-pre-work-dialog/meeting-pre-work-dialog.component";
import { MeetingsService } from "./meetings.service";
import { MoveMeetingTemplateDialogComponent } from "./move-meeting-template-dialog/move-meeting-template-dialog.component";
import { IScheduleMeetingWorkflowRunData, scheduleMeetingWorkflow } from "./schedule-meeting-workflow/schedule-meeting-workflow";

@Injectable({
    providedIn: "root",
})
export class MeetingsUiService {
    // default to using active meeting page on hq
    private useActiveMeetingPageSubject = new BehaviorSubject<boolean>(AdaptClientConfiguration.AdaptProject === AdaptProject.Alto);

    public constructor(
        private commonDialogService: AdaptCommonDialogService,
        private meetingsService: MeetingsService,
        private workflowService: WorkflowService,
        private workflowMeetingService: WorkflowMeetingService,
        private orgService: OrganisationService,
        private navHierarchyService: NavigationHierarchyService,
        private shellUiService: ShellUiService,
        private routeService: RouteService,
        private kanbanAuthService: KanbanAuthService,
        private calendarIntegrationUtilities: CalendarIntegrationUtilities,
        private linkService: LinkService,
        @Inject(TEAM_DASHBOARD_PAGE) private teamDashboardPageRoute: IAdaptRoute<{ teamId: number }>,
    ) {
    }

    public startMeeting(meeting: Meeting, callbackAfterSave?: () => any) {
        return this.meetingsService.saveEntities(meeting.extensions.startMeeting()).pipe(
            tap(() => {
                if (FunctionUtilities.isFunction(callbackAfterSave)) {
                    callbackAfterSave();
                }
            }),
            switchMap(() => this.isUsingActiveMeetingPage),
            tap((isUsingActiveMeetingPage) => {
                if (isUsingActiveMeetingPage) {
                    this.meetingsService.gotoActiveMeetingPage(meeting, undefined, true);
                } else if (this.kanbanAuthService.canEditPrivateBoardsForTeam(meeting.team!)) {
                    teamKanbanPageRoute.gotoRoute({ teamId: meeting.teamId });
                } else {
                    this.teamDashboardPageRoute.gotoRoute({ teamId: meeting.teamId });
                }
            }),
        );
    }

    public moveMeetingToActivePage(meeting: Meeting, returnWorkflowPath?: string, showDescription = false) {
        this.useActiveMeetingPage(true);
        if (meeting) {
            return this.meetingsService.gotoActiveMeetingPage(meeting, returnWorkflowPath, showDescription);
        }

        return EMPTY;
    }

    public moveMeetingToSidebar(meeting: Meeting) {
        this.useActiveMeetingPage(false);

        setTimeout(() => {
            this.shellUiService.focusTab(MeetingTabId);
        });

        return this.meetingsService.gotoTeamMeetingsPage(meeting.team!.teamId);
    }

    public useActiveMeetingPage(useActiveMeetingPage: boolean) {
        this.useActiveMeetingPageSubject.next(useActiveMeetingPage);
        if (useActiveMeetingPage) {
            const tab = AdaptClientConfiguration.AdaptProject === AdaptProject.Alto ? MenuTabId : TeamTabId;
            this.shellUiService.focusTab(tab);
        }
    }

    public get isUsingActiveMeetingPage$() {
        return this.useActiveMeetingPageSubject.asObservable();
    }

    public get isUsingActiveMeetingPage() {
        return this.isUsingActiveMeetingPage$.pipe(take(1));
    }

    public editMeetingAgendaItem(agendaItem: MeetingAgendaItem, saveOnClose: boolean) {
        return this.commonDialogService.open(EditAgendaItemDialogComponent, { agendaItem, saveOnClose });
    }

    public editAgendaTemplate(agendaTemplate: MeetingAgendaTemplate) {
        return this.commonDialogService.open(EditAgendaTemplateDialogComponent, agendaTemplate);
    }

    public moveMeetingTemplate(template: MeetingAgendaTemplate) {
        return this.commonDialogService.open(MoveMeetingTemplateDialogComponent, { template });
    }

    public copyMeetingTemplate(template: MeetingAgendaTemplate) {
        return this.commonDialogService.open(MoveMeetingTemplateDialogComponent, { template, isCopying: true });
    }

    public importMeetingAgenda(data: IImportMeetingAgendaData) {
        return this.commonDialogService.open(ImportAgendaDialogComponent, data);
    }

    public importMeetingFromCalendarIntegration(teamId: number) {
        return this.commonDialogService.open(MeetingIntegrationImportDialogComponent, { teamId }).pipe(
            switchMap((meeting) => this.editMeeting(meeting)),
        );
    }

    public linkExistingCalendarIntegrationMeeting(meeting: Meeting) {
        return this.commonDialogService.open(MeetingIntegrationImportDialogComponent, { teamId: meeting.teamId, linkToMeeting: meeting });
    }

    public detachCalendarIntegrationMeeting(meeting: Meeting, provider: ICalendarProvider) {
        return this.commonDialogService.openConfirmationDialog({
            title: `Detach ${provider.name} event from meeting`,
            message: `<p>Are you sure you want to detach the ${provider.name} event from this meeting?</p>
                <p>You can re-attach it anytime via the meeting menu.</p>`,
        }).pipe(
            tap(() => this.calendarIntegrationUtilities.detachMicrosoftUniqueIdFromMeeting(meeting)),
            switchMap(() => this.meetingsService.saveEntities([meeting])),
        );
    }

    public showMeetingPreWork(meeting: Meeting) {
        return this.commonDialogService.open(MeetingPreWorkDialogComponent, meeting);
    }

    public createMeetingNoteForAgendaItem(agendaItem: MeetingAgendaItem, type: MeetingNoteType) {
        return this.meetingsService.createMeetingNoteForAgendaItem(agendaItem, type).pipe(
            switchMap((meetingNote) => {
                const dialogData: IEditSimpleValueBreezeEntityDialogData = {
                    title: `Add ${type}`,
                    entities: [{
                        label: type,
                        entity: meetingNote,
                        fieldName: "content",
                        type: SimpleValueType.RichText,
                    }],
                    articleId: MeetingNoteTypeMetadata.ArticleId[type],
                    dialogWidth: 900,
                    saveOnClose: true,
                };
                return this.commonDialogService.open(EditSimpleValueBreezeEntityDialogComponent, dialogData);
            }),
        );
    }

    public createMeeting(teamId: number) {
        return this.meetingsService.createMeeting(teamId).pipe(
            switchMap((meeting) => this.editMeeting(meeting, undefined, false, true)),
        );
    }

    public editMeeting(meeting: Meeting, titleOverride?: string, rescheduling = false, isCreatingNewMeeting = false) {
        return this.commonDialogService.open(WorkflowRunDialogComponent, {
            workflow: scheduleMeetingWorkflow,
            runData: { meeting, rescheduling, createTeamsMeeting: false, isCreatingNewMeeting } as IScheduleMeetingWorkflowRunData,
            titleOverride,
        }).pipe(
            map(() => meeting),
        );
    }

    public editMeetingAgenda(meeting: Meeting) {
        return this.commonDialogService.open(EditMeetingAgendaDialogComponent, meeting);
    }

    public promptForEndedMeetingAgendaEdit() {
        return this.commonDialogService.openConfirmationDialog({
            title: "Edit ended meeting agenda",
            message: `<p>Are you sure you want to edit the agenda of this <b>ENDED</b> meeting?</p>
                        <p><small>Meetings were recorded with the participation of other attendees.
                        Please ensure all involved are aware of the edited contents.</small></p>`,
            confirmButtonText: "Continue with Edit",
            cancelButtonText: "Cancel",
        });
    }

    @Autobind
    public endMeeting(meeting: Meeting, returnPath?: string) {
        const confirmationDialogData: IConfirmationDialogData = {
            title: "Ending meeting",
            message: "Are you sure you want to end the meeting?",
            confirmButtonText: "End",
            cancelButtonText: "Cancel",
        };

        return this.commonDialogService.openConfirmationDialogWithBoolean(confirmationDialogData).pipe(
            filter((stopMeeting) => !!stopMeeting),
            switchMap(() => this.meetingsService.saveEntities(meeting.extensions.endMeeting())),
            switchMap(() => returnPath
                ? of(returnPath)
                : this.workflowMeetingService.getMeetingWorkflowConnection(meeting).pipe(
                    switchMap((workflowConnection) => workflowConnection
                        ? this.workflowService.getPersonalPageWithContinueWorkflow(workflowConnection.workflowId).pipe(
                            map((route) => route.url))
                        : of(returnPath)),
                )),
            switchMap((path) => path
                ? this.routeService.navigateByUrl(path)
                : this.meetingsService.gotoMeetingPage(meeting)),
            tap(() => {
                this.useActiveMeetingPage(AdaptClientConfiguration.AdaptProject === AdaptProject.Alto);
                this.navHierarchyService.updateActiveNodeFromUrl();
                this.orgService.notifyOrganisationChangedEvent(); // this will trigger tab switching when node already active
            }),
        );
    }

    @Autobind
    public rescheduleMeeting(meeting: Meeting) {
        return this.editMeeting(this.meetingsService.rescheduleMeeting(meeting), "Reschedule meeting", true).pipe(
            switchMap(() => this.meetingsService.gotoMeetingPage(meeting)),
            tap(() => {
                this.useActiveMeetingPage(AdaptClientConfiguration.AdaptProject === AdaptProject.Alto);
                this.navHierarchyService.updateActiveNodeFromUrl();
                this.orgService.notifyOrganisationChangedEvent(); // this will trigger tab switching when node already active
            }),
        );
    }

    public saveAsMeetingTemplate(meeting: Meeting) {
        return this.commonDialogService.open(NewMeetingTemplateDialogComponent, meeting);
    }

    public openItemLinkDialog(agendaItem: MeetingAgendaItem, saveAfterAdd = true) {
        return this.meetingsService.getActionLinksForAgendaItem(agendaItem).pipe(
            switchMap((existingLinks) => {
                const excludedItemIds = existingLinks
                    .filter((link) => !!link.item)
                    .map((link) => link.itemId!);
                return this.commonDialogService.open(SelectItemDialogComponent, {
                    team: agendaItem.meeting?.team,
                    linking: true,
                    excludedItemIds,
                });
            }),
            switchMap((result) => {
                const destItems = result.items;
                if (destItems.length > 0) {
                    return forkJoin(destItems.map((destItem) => this.linkService.createLinkForAgendaItem(agendaItem, destItem.itemId)));
                }

                return of([]);
            }),
            switchMap((links) => {
                if (saveAfterAdd) {
                    return this.meetingsService.saveEntities(links).pipe(
                        map(() => links),
                    );
                } else {
                    return of(links);
                }
            }),
            catchError((err: AdaptError) => this.commonDialogService.showMessageDialog("Error saving link", err.message).pipe(
                switchMap(() => throwError(() => err)),
            )),
        );
    }

    public openObjectiveLinkDialog(agendaItem: MeetingAgendaItem) {
        const unsavedLinks: Link[] = [];
        return this.meetingsService.getObjectiveLinksForAgendaItem(agendaItem).pipe(
            switchMap((links) => this.commonDialogService.open(AddObjectiveLinkDialogComponent, {
                disabledObjectives: links
                    .map((link) => link.objective1!),
            } as IAddObjectiveLinkDialogData)),
            switchMap((selectedObjectives) => {
                if (!selectedObjectives || selectedObjectives.length === 0) {
                    return of(undefined);
                }

                return forkJoin(selectedObjectives.map((destObjective) => this.linkService.createObjectiveLinkForAgendaItem(agendaItem, destObjective))).pipe(
                    tap((links) => unsavedLinks.push(...links)),
                    switchMap((newObjectiveLinks) => this.meetingsService.saveEntities(newObjectiveLinks)),
                );
            }),
            catchError((err: AdaptError) => this.commonDialogService.showMessageDialog("Error saving link", err.message).pipe(
                switchMap(() => unsavedLinks.length > 0
                    ? this.meetingsService.rejectChanges(unsavedLinks) // just a few selected objectives to link - not even persisted; so just discard them if saving fails
                    : of(undefined)),
                switchMap(() => throwError(() => err)))),
        );
    }
}
